import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { obtenerTokenDeSesion } from "../../helpers/authToken";
import { Card, CardBody, Spinner } from "reactstrap";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import buildDataSummary from "./buildDataSummary";
// El encabezado de las tablas que devuelve el asistente es el último lugar
// donde quedaba el azul viejo: con el de la paleta, las tablas del chat y los
// gráficos de al lado son de la misma familia.
import { ORIGEN } from "../Stats2/theme";

const aiMessageStyles = `
.ai-message p { margin: 0 0 0.5em; }
.ai-message p:last-child { margin-bottom: 0; }
.ai-message h1, .ai-message h2, .ai-message h3 { font-size: 14px; font-weight: 700; margin: 0.8em 0 0.4em; }
.ai-message h1:first-child, .ai-message h2:first-child, .ai-message h3:first-child { margin-top: 0; }
.ai-message ul, .ai-message ol { margin: 0.3em 0; padding-left: 1.4em; }
.ai-message li { margin-bottom: 0.2em; }
.ai-message strong { font-weight: 600; }
.ai-message table { width: 100%; border-collapse: collapse; margin: 0.5em 0; font-size: 12px; }
.ai-message th, .ai-message td { border: 1px solid #dee2e6; padding: 4px 8px; text-align: left; }
.ai-message th { background: ${ORIGEN.tickets.color}; color: #fff; font-weight: 600; }
.ai-message tr:nth-child(even) { background: #f8f9fa; }
.ai-message tr:hover { background: #e9ecef; }
.ai-message code { background: #e9ecef; padding: 1px 4px; border-radius: 3px; font-size: 12px; }
.ai-message hr { margin: 0.5em 0; border-color: #dee2e6; }
`;

const FUNCTIONS_URL = process.env.NODE_ENV === "development"
  ? "http://127.0.0.1:5001/ticketcontroldev/us-central1/api"
  : "https://us-central1-ticketcontroldev.cloudfunctions.net/api";

const SUGGESTED_QUESTIONS = [
  "¿Cómo estuvieron los ingresos esta semana comparados con la anterior?",
  "¿Cuál es mi mejor día de la semana?",
  "¿Cuánto estoy perdiendo en cancelaciones?",
  "¿Cómo viene este mes comparado con el anterior?",
  "¿Cuál fue mi mejor día del último mes?",
  "¿De dónde vienen mis ingresos? (tickets, abonados, acuerdos)",
];

const MessageBubble = ({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`d-flex ${isUser ? "justify-content-end" : "justify-content-start"} mb-3`}>
      {!isUser && (
        <div
          className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0 me-2"
          style={{ width: 32, height: 32, marginTop: 2 }}
        >
          <span className="material-symbols-rounded text-primary" style={{ fontSize: 18 }}>smart_toy</span>
        </div>
      )}
      <div
        className={`px-3 py-2 rounded-3 ${
          isUser
            ? "bg-primary text-white"
            : "bg-light border ai-message"
        }`}
        style={{
          maxWidth: "80%",
          fontSize: 13,
          lineHeight: 1.5,
          wordBreak: "break-word",
        }}
      >
        {isUser ? msg.text : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => (
                    <div style={{ overflowX: "auto", margin: "0.5em 0", borderRadius: 6, border: "1px solid #dee2e6" }}>
                      <table>{children}</table>
                    </div>
                  ),
                }}
              >
                {msg.text}
              </ReactMarkdown>
            )}
      </div>
    </div>
  );
};

const TabAsistente = ({ dailyDocs, monthlyDocs, liveData, companyId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hideAiInfo, setHideAiInfo] = useState(() => localStorage.getItem("stats_hide_ai_info") === "1");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const dataSummary = useMemo(
    () => buildDataSummary(dailyDocs, monthlyDocs, liveData),
    [dailyDocs, monthlyDocs, liveData]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendQuestion = useCallback(async (question) => {
    if (!question.trim() || loading) return;

    const userMsg = { role: "user", text: question.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      // El endpoint exige una sesion real de Firebase: gasta con nuestra clave
      // de Anthropic y su URL esta en el bundle publico. Sin token, 401.
      const token = await obtenerTokenDeSesion();
      const response = await fetch(`${FUNCTIONS_URL}/Monitor/ai-assistant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-client-key": companyId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: question.trim(),
          dataSummary,
          context: "stats",
        }),
      });

      const data = await response.json();

      if (data.success && data.answer) {
        setMessages((prev) => [...prev, { role: "assistant", text: data.answer }]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", text: data.error || "No pude procesar la consulta. Intenta nuevamente." },
        ]);
      }
    } catch (err) {
      console.error("AI Assistant error:", err);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Error de conexión. Verifica tu internet e intenta nuevamente." },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, dataSummary, companyId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuestion(input);
  };

  const handleSuggestion = (q) => {
    sendQuestion(q);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendQuestion(input);
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <>
      <style>{aiMessageStyles}</style>
      {/* Info banner */}
      {!hideAiInfo && (
      <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded bg-light border" style={{ fontSize: 12, position: "relative" }}>
        <span className="material-symbols-rounded text-primary" style={{ fontSize: 16 }}>info</span>
        <span className="text-muted" style={{ flex: 1 }}>
          El asistente analiza los datos de tu estacionamiento para responder preguntas.
          No almacena las conversaciones. Los datos se envian de forma segura.
        </span>
        <button type="button" className="btn-close ms-2" style={{ fontSize: 8 }} onClick={() => { setHideAiInfo(true); localStorage.setItem("stats_hide_ai_info", "1"); }} />
      </div>
      )}

      <Card className="shadow-sm" style={{ minHeight: 500 }}>
        <CardBody className="d-flex flex-column p-0" style={{ height: "calc(100vh - 380px)", minHeight: 450 }}>
          {/* Messages area */}
          <div className="flex-grow-1 overflow-auto p-3">
            {isEmpty ? (
              <div className="text-center py-4">
                <span
                  className="material-symbols-rounded text-primary d-block mb-3"
                  style={{ fontSize: 48, opacity: 0.6 }}
                >
                  smart_toy
                </span>
                <h5 className="text-muted mb-2">Asistente de Estadísticas</h5>
                <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                  Pregúntame sobre los datos de tu estacionamiento.
                  Puedo analizar ingresos, comparar períodos, identificar tendencias y más.
                </p>

                {/* Suggested questions */}
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {SUGGESTED_QUESTIONS.map((q, i) => (
                    <button
                      key={i}
                      className="btn btn-outline-primary btn-sm rounded-pill"
                      style={{ fontSize: 12 }}
                      onClick={() => handleSuggestion(q)}
                      disabled={loading}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <MessageBubble key={i} msg={msg} />
                ))}
                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div
                      className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: 32, height: 32 }}
                    >
                      <span className="material-symbols-rounded text-primary" style={{ fontSize: 18 }}>smart_toy</span>
                    </div>
                    <div className="bg-light border rounded-3 px-3 py-2">
                      <Spinner size="sm" color="primary" className="me-2" />
                      <span className="text-muted" style={{ fontSize: 13 }}>Analizando datos...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input area */}
          <div className="border-top p-3 bg-light">
            <form onSubmit={handleSubmit} className="d-flex gap-2">
              <input
                ref={inputRef}
                type="text"
                className="form-control"
                placeholder="Escribe tu pregunta sobre el estacionamiento..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
                style={{ fontSize: 13 }}
                autoFocus
              />
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-1 px-3"
                disabled={loading || !input.trim()}
              >
                <span className="material-symbols-rounded" style={{ fontSize: 18 }}>send</span>
              </button>
            </form>
            {/* <div className="text-muted text-center mt-2" style={{ fontSize: 10 }}>
              Powered by Claude AI — Las respuestas son orientativas y basadas en los datos disponibles
            </div> */}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default TabAsistente;
